import { useEffect } from 'react';
import { create } from 'zustand';

/**
 * How large the interface is drawn.
 *
 * The interface has a deliberately compact desktop density. On a larger panel
 * the same CSS values become physically smaller than intended, so automatic
 * mode scales the complete layout rather than enlarging text in isolation.
 *
 * ## Why `zoom` and not a font scale
 *
 * Scaling type alone leaves every padding, border, avatar and icon where it
 * was, so the layout stops being the layout: 15px text in a 26px row is not a
 * larger version of that row, it is a broken one. The stylesheet is written in
 * shared spacing and type tokens throughout, so the honest lever is the one that
 * multiplies all of them.
 *
 * `zoom` on the root element does exactly that, and — unlike `transform:
 * scale()` — the initial containing block is scaled with it, so `100vh` still
 * means the window and nothing overflows. Verified in the browser at 0.85 and
 * 1.25 before this was written, because that guarantee is the whole reason the
 * property is usable here.
 *
 * ## Why this is stored per device
 *
 * A laptop and a desktop want different answers, and they belong to the same
 * account. Following the account would make one of the two wrong every time
 * somebody sat down. Same reasoning as the theme.
 */

export type UiScale = 'auto' | number;

const STORAGE_KEY = 'aksolot.ui-scale';

/** The frame the design was drawn in. Everything below is relative to it. */
export const DESIGN_WIDTH = 1600;
export const DESIGN_HEIGHT = 1000;

export const MIN_SCALE = 0.8;
export const MAX_SCALE = 1.6;

/**
 * How small automatic mode is willing to go.
 *
 * Above the slider's own floor, and deliberately. Shrinking is how you fit more
 * on a laptop, and it is also how 13px body text becomes 10px — so the machine
 * is allowed to do a little of it and a person is allowed to do more. Anyone
 * who genuinely wants maximum density can drag past this; nothing should land
 * there without being asked for.
 */
export const AUTO_MIN_SCALE = 0.9;

/**
 * The scale a screen gets when nobody has chosen one.
 *
 * Driven by whichever axis is tighter, so a wide-but-short window does not get
 * an interface too tall to fit in it.
 *
 * ## The two directions are not symmetric
 *
 * Growing is free: a 1440p panel has the room, and at 100% the interface sits
 * in the corner of it looking like a screenshot of a smaller computer. So above
 * the design size the scale follows the screen exactly, to 160%.
 *
 * Shrinking costs readability, and the layout already gives back most of what a
 * narrow window needs — the member list leaves at 1100px, the sidebar becomes a
 * drawer at 760px. So below the design size the scale moves at *half* rate and
 * stops at 90%: a 1366×768 laptop lands at 90% with its member list folded away
 * rather than at 77% with everything technically on screen and nothing legible.
 *
 * Rounded to 5% steps so a window dragged one pixel wider does not reflow the
 * entire interface — and so the number shown in settings is one a person can
 * read back.
 */
export function autoScale(width: number, height: number): number {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return 1;
  /*
   * A phone is never scaled.
   *
   * Two reasons, and either alone would be enough. Shrinking the interface on
   * the one device where every target is hit with a thumb works directly
   * against the 48px minimum the phone layout is built on. And `zoom` on the
   * root scales the coordinate space that `position: fixed` resolves in, so
   * the bottom bar and the call strip — the two things a phone user reaches
   * for most — would sit below the fold at anything under 100%.
   */
  if (width <= MOBILE_WIDTH) return 1;
  const fit = Math.min(width / DESIGN_WIDTH, height / DESIGN_HEIGHT);
  const eased = fit >= 1 ? fit : 1 - (1 - fit) / 2;
  const stepped = Math.round(eased * 20) / 20;
  return Math.min(MAX_SCALE, Math.max(AUTO_MIN_SCALE, stepped));
}

/** Matches the phone breakpoint in the stylesheet. Changing one means both. */
export const MOBILE_WIDTH = 760;

export function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

/** The percentage shown in settings, for either mode. */
export function resolveScale(scale: UiScale, width: number, height: number): number {
  // The manual slider obeys it too: a stored 80% from a laptop would otherwise
  // follow the account onto a phone and push the bars off screen.
  if (width <= MOBILE_WIDTH) return 1;
  return scale === 'auto' ? autoScale(width, height) : clampScale(scale);
}

/**
 * The zoom currently on the root element, as a number.
 *
 * Read back from the DOM rather than from the store, because the callers are
 * measuring the document and have to agree with whatever it is actually
 * wearing — including on the first frame, before React has re-rendered.
 */
export function rootZoom(): number {
  if (typeof document === 'undefined') return 1;
  const raw = document.documentElement.style.getPropertyValue('--ui-scale');
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

/**
 * Converts a viewport coordinate into the CSS pixels a positioned element needs.
 *
 * ## The mismatch this exists to fix
 *
 * Under root `zoom`, the two coordinate systems in a browser stop agreeing:
 *
 *   - `event.clientX`, `getBoundingClientRect()` and `window.innerWidth` stay in
 *     *viewport* pixels — zoom does not touch them.
 *   - a `left: 800px` written on a fixed element *inside* the zoomed root is
 *     multiplied by the zoom, and lands at 1040 on screen at 1.3×.
 *
 * So a popover that put the cursor's `clientX` straight into `left` opened
 * further right the more the interface was scaled up, and at 1.3× on a 1440px
 * screen the right-hand menus were off the edge entirely. Measured, not
 * guessed: a fixed probe at `left: 100px` reports `rect.left = 130` at 1.3×.
 *
 * Every popover positioned from a pointer or from a measured rect goes through
 * here. Doing the arithmetic in viewport pixels and converting once, at the
 * point of writing the style, keeps one space in the maths and one at the edge.
 */
export function toRootPx(viewportPx: number): number {
  return viewportPx / rootZoom();
}

function load(): UiScale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null || stored === 'auto') return 'auto';
    const parsed = Number(stored);
    // A stored value that is not a number is a corrupted or hand-edited entry,
    // and "auto" is a better answer than an interface at NaN× that renders as
    // nothing at all.
    return Number.isFinite(parsed) ? clampScale(parsed) : 'auto';
  } catch {
    // Private browsing. The default applies; only its memory is lost.
  }
  return 'auto';
}

interface UiScaleStore {
  scale: UiScale;
  set: (scale: UiScale) => void;
}

export const useUiScale = create<UiScaleStore>((set) => ({
  scale: load(),
  set: (scale) => {
    const next = scale === 'auto' ? 'auto' : clampScale(scale);
    set({ scale: next });
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // As above.
    }
  },
}));

/**
 * Applies the scale, and keeps following the window while the choice is auto.
 *
 * The resize listener only exists in auto mode, for the same reason the theme
 * only subscribes to the OS while it is set to follow it: an explicit choice
 * must not be overwritten by the environment changing.
 */
export function useApplyUiScale(): void {
  const scale = useUiScale((s) => s.scale);

  useEffect(() => {
    const apply = () => {
      const resolved = resolveScale(scale, window.innerWidth, window.innerHeight);
      /*
       * Written as a string, and 1 is written as "1" rather than removed: the
       * element also carries the value in a custom property so anything that
       * needs to reason in real pixels (a popover measuring the viewport) can
       * read it instead of guessing.
       */
      document.documentElement.style.setProperty('zoom', String(resolved));
      document.documentElement.style.setProperty('--ui-scale', String(resolved));
    };

    apply();
    if (scale !== 'auto') return;
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [scale]);
}
